import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getExtremelyShortScreen from '../selectors/getExtremelyShortScreen';
import getProfile from '../selectors/getProfile';
import combineStyles from '../utils/combineStyles';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import Text from '../components/Text';
import OrderingWrapper from '../components/OrderingWrapper';
import OrderSummary from '../components/OrderSummary';
import OrderTotals from '../components/OrderTotals';
import PlaceOrderButton from '../components/PlaceOrderButton';
import SendVerificationButton from '../components/SendVerificationButton';
import { initiateCheckout, purchase } from '../utils/analytics';
import lodash from 'lodash';
import { useTyro } from '../hooks/TyroProvider';
import { setCheckoutInProgress } from '../slices/checkoutInProgress';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { getRedcatPaySaleFailureMessage } from '../utils/errors';
import { deliveryFee } from '../thunks/deliveryFee';
import { useRedcatPayment } from '../shared/redcatpayments/hooks/useRedcatPayment';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { MESSAGE_MODAL_ID } from '../modals/MessageModal';

const {
  getMember,
  getGuestOrderingAvailable,
  getStagedPurchases,
  getRootCategory,
  getBrands,
  getOrderTotals,
  getGatewayType,
  getSelectedPaymentMethods,
  getTyroPaymentStatus,
  getOpenPurchase,
  getPurchaser,
  getPurchaserInvalidReasons,
  getSaleType,
  getLocationId,
  getSelectedOffer,
  getFreeOrderReadyToSubmit,
  getLocation,
} = OrderingSelectors;

const {
  fetchStockBalances,
  updateKeyOrderProperty,
  resetSelectedPaymentMethods,
  resetOrder,
  setTyroPaymentStatus,
  tyroSale,
  sale,
} = OrderingOperations;

const {
  PAYMENT_GATEWAY_TYPES,
  TYRO_PAYMENT_TOKEN_STATUS,
  PAYMENT_METHOD,
  SALE_TYPE,
  PAYMENT_SESSION_STATUS,
} = OrderingConstants;

export const REVIEW_ORDER_SCREEN_ROUTE = '/review-order';

export const processId = (a?: number) => (a ? String(a) : undefined);

const ReviewOrderScreen = () => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const gatewayType = useAppSelector(getGatewayType);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const extremelyShortScreen = useAppSelector(getExtremelyShortScreen);
  const enableStockPolling = useAppSelector(state => state.config.enableStockPolling);
  const stockPollingInterval = useAppSelector(state => state.config.stockPollingInterval);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const [checkPLUQuantityOnLoad, setCheckPLUQuantityOnLoad] = useState(true);
  const profile = useAppSelector(getProfile);
  const member = useAppSelector(getMember);
  const guestOrderingAvailable = useAppSelector(getGuestOrderingAvailable);
  const orderTotals = useAppSelector(getOrderTotals);
  const stagedPurchases = useAppSelector(getStagedPurchases);
  const brands = useAppSelector(getBrands);
  const rootCategory = useAppSelector(getRootCategory);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const location = useAppSelector(getLocation);
  const payingWithGoogleOrApplePay = selectedPaymentMethods.some(({ method }) =>
    [PAYMENT_METHOD.APPLE_PAY, PAYMENT_METHOD.GOOGLE_PAY].includes(method),
  );

  const tyroPaymentStatus = useAppSelector(getTyroPaymentStatus);
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const openPurchase = useAppSelector(getOpenPurchase);
  const purchaserDetails = useAppSelector(getPurchaser);
  const saleType = useAppSelector(getSaleType);
  const locationId = useAppSelector(getLocationId);
  const selectedOffer = useAppSelector(getSelectedOffer);
  const freeOrderReadyToSubmit = useAppSelector(getFreeOrderReadyToSubmit);
  const tyroPollingRules = useAppSelector(state => state.config.tyroPollingRules);
  const disableFetchDeliveryFeeApi = useAppSelector(state => state.disableFetchDeliveryFeeApi);

  const categories = rootCategory?.subCategories || [];
  const purchaserInvalidReasons = useAppSelector(getPurchaserInvalidReasons);

  const runUpdateKeyOrderPropertyAndFetchDeliveryFee = async () => {
    if (saleType === SALE_TYPE.DELIVERY) {
      await dispatch(
        updateKeyOrderProperty({ autoApply: true, updateDeliveryEstimate: true }),
      ).unwrap();

      if (!disableFetchDeliveryFeeApi) {
        await dispatch(deliveryFee()).unwrap();
      }
    }
  };

  const dispatch = useAppDispatch();

  /* Resetting selectedPaymentMethods on mount and if location or saleType changes
  because for tyro we want to regenerate pay secrets again with updated location and sale type 
  
  Here using locationId dependency instead of location because using location causes unecessary re-renders

  * Check if its a free order. If it's a free order remove all the existing 
  * selected payment methods. So later on handleCheckout() the free 
  * paymentMethod gets added to the list
  */
  useEffect(() => {
    if (selectedPaymentMethods.length) {
      dispatch(resetSelectedPaymentMethods({}));
    }
  }, [locationId, saleType, freeOrderReadyToSubmit]);

  const handleBeginCheckoutAnalytics = () => {
    initiateCheckout({
      items: stagedPurchases.map(purchaseItem => {
        const category =
          categories.find(category =>
            lodash.map(category.items, 'id').find(id => id === purchaseItem.item.id),
          )?.name || null;
        const brand = brands.find(({ id }) => purchaseItem.brandId === id)?.name || null;
        return {
          ...(purchaseItem.item.sizes.find(item => item.id == purchaseItem.sizeId) ||
            purchaseItem.item),
          quantity: purchaseItem.quantity,
          totalPrice: purchaseItem.moneyPrice,
          choicesWithQuantity: purchaseItem.choicesWithQuantity,
          brand,
          category,
        };
      }),
      total: orderTotals!.discountedMoneyPrice,
      member,
      store: location?.name,
      saleType,
      selectedOffer,
    });
  };

  useEffect(() => {
    runUpdateKeyOrderPropertyAndFetchDeliveryFee();

    handleBeginCheckoutAnalytics();
    if (checkPLUQuantityOnLoad) {
      dispatch(fetchStockBalances());
      setCheckPLUQuantityOnLoad(false);
    }
    if (enableStockPolling && stockPollingInterval) {
      interval.current = setInterval(
        () => dispatch(fetchStockBalances()),
        stockPollingInterval * 1000,
      );
      return () => {
        clearInterval(interval.current as NodeJS.Timeout);
      };
    }
  }, []);

  const useMobileScreen = deviceTypeMobile || extremelyShortScreen;

  const mustBeVerifiedMember = !profile?.verified && !guestOrderingAvailable;

  // gatewayType is fixed for the lifetime of this screen (set once from config before mount),
  // so this conditional hook call always executes in the same order across renders.
  const {
    pollingReference: redcatPollingReference,
    paymentStatus: redcatPayStatus,
    paymentComplete: redcatPayComplete,
    paymentTimedOut: redcatPayTimedOut,
    resetPaymentAttempt: resetRedcatPaymentAttempt,
    resumePaymentStatusPolling: resumeRedcatPaymentStatusPolling,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useRedcatPayment()
    : {
        pollingReference: null,
        paymentStatus: null,
        paymentComplete: false,
        paymentTimedOut: false,
        resetPaymentAttempt: () => {},
        resumePaymentStatusPolling: () => {},
      };

  // Getting tyro payment data from the hook
  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const {
    tyroJSRef,
    tyroPaymentOrderId,
    setTyroPaymentOrderId,
    setExternalDependency,
    hasPaySecret,
    showAppleGooglePayBtn,
    setShowAppleGooglePayBtn,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  const checkoutSuccessful = (transactionId?: string) => {
    dispatch(setCheckoutInProgress(false));

    purchase({
      items: stagedPurchases.map(purchaseItem => {
        const category =
          categories.find(category =>
            lodash.map(category.items, 'id').find(id => id === purchaseItem.item.id),
          )?.name || null;

        const brand = brands.find(({ id }) => purchaseItem.brandId === id)?.name || null;
        return {
          ...(purchaseItem.item.sizes.find(item => item.id == purchaseItem.sizeId) ||
            purchaseItem.item),
          quantity: purchaseItem.quantity,
          totalPrice: purchaseItem.moneyPrice,
          choicesWithQuantity: purchaseItem.choicesWithQuantity,
          category,
          brand,
        };
      }),
      total: orderTotals?.discountedMoneyPrice || 0,
      transactionId,
      brand: brands.find(brand => brand.id === openPurchase?.brandId)?.name,
      member,
      selectedOffer,
      store: location?.name,
      saleType,
      purchaseType: 'Food',
    });
  };

  const fetchPaymentStatusInterval = useRef<NodeJS.Timeout | null>(null);
  const readyInterval = useRef<NodeJS.Timeout | null>(null);
  const paymentStatusPollCount = useRef(0);

  const fetchTokenStatus = () => {
    if (!tyroPaymentOrderId.length) {
      return;
    }

    // Stop polling if max polling count hits
    if (paymentStatusPollCount.current >= tyroPollingRules.maxCount) {
      clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
      enqueueErrorSnackbar(`Payment polling status check timed out. ${t('chargedMessage.maybe')}`);
      dispatch(setCheckoutInProgress(false));
      setTyroPaymentOrderId('');
      dispatch(setTyroPaymentStatus(null));
      dispatch(resetOrder({ preserveMember: false }));

      return;
    }

    if (
      !tyroPaymentStatus ||
      (tyroPaymentStatus &&
        (tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.PENDING ||
          tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.PROCESSING))
    ) {
      dispatch(
        tyroSale({
          orderId: tyroPaymentOrderId,
          authenticationMethod: member ? 'member' : 'none',
          shouldSetSaleDetails: true,
        }),
      );
    } else {
      clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
    }

    // Incrementing polling count
    paymentStatusPollCount.current += 1;
  };

  useEffect(() => {
    // Polling tyro payment status from BE
    if (
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
      checkoutInProgress &&
      tyroPaymentOrderId.length
    ) {
      fetchPaymentStatusInterval.current = setInterval(fetchTokenStatus, tyroPollingRules.interval);

      fetchTokenStatus();

      return () => {
        clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
        clearInterval(readyInterval.current as NodeJS.Timeout);
      };
    }
  }, [gatewayType, checkoutInProgress, tyroPaymentOrderId]);

  useEffect(() => {
    if (tyroPaymentStatus) {
      if (tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
        checkoutSuccessful(processId(tyroPaymentStatus.Data.OrderId));
        setTyroPaymentOrderId('');
        dispatch(setTyroPaymentStatus(null));
      } else if (
        tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.FAILED ||
        tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.VOIDED
      ) {
        const statusErrorMessage = tyroPaymentStatus.Data.error || `Something went wrong`;
        enqueueErrorSnackbar(`${statusErrorMessage}. ${t('chargedMessage.maybe')}`);
        dispatch(setCheckoutInProgress(false));
        setTyroPaymentOrderId('');
        dispatch(setTyroPaymentStatus(null));
        dispatch(resetOrder({ preserveMember: false }));
      }
    }
  }, [tyroPaymentStatus]);

  useEffect(() => {
    if (!redcatPayComplete) return;

    if (redcatPayStatus !== PAYMENT_SESSION_STATUS.PAYMENT_SUCCESS) {
      enqueueErrorSnackbar(t('paymentFailedMessage'));
      dispatch(setCheckoutInProgress(false));
      resetRedcatPaymentAttempt();
      return;
    }

    dispatch(
      sale({
        route: 'checkout',
        authenticationMethod: member ? 'member' : 'none',
        paymentProvider: 'redcat-payments',
        paymentReference: redcatPollingReference ?? undefined,
      }),
    )
      .unwrap()
      .then((response: any) => {
        checkoutSuccessful(
          processId(
            response?.data?.TransactionID || response?.data?.ID || response?.data?.transactionId,
          ),
        );
      })
      .catch((err: any) => {
        const failureMessage = getRedcatPaySaleFailureMessage(err, {
          refundFailedMessage: t('refundFailedMessage'),
          fallbackMessage: t('orderSubmissionFailedMessage'),
        });
        enqueueErrorSnackbar(failureMessage);
        dispatch(setCheckoutInProgress(false));
        resetRedcatPaymentAttempt();
      });
  }, [redcatPayComplete]);

  useEffect(() => {
    if (!redcatPayTimedOut) return;
    // Timeout ≠ confirmed failure — the charge may have succeeded server-side after we
    // stopped polling. Keep the session/idempotencyKey alive so retrying resumes polling
    // on the same reference instead of firing a brand-new charge. The modal has no close
    // button, so the consumer can't reach the cart or payment methods while the original
    // payment is unresolved — Retry is the only way out.
    dispatch(setCheckoutInProgress(false));
    dispatch(
      setCurrentModal({
        modalId: MESSAGE_MODAL_ID,
        params: {
          message: t('paymentTimeoutMessage'),
          hideCloseButton: true,
          alertButtons: [
            {
              text: t('button.retry'),
              isPreferred: true,
              action: () => {
                dispatch(setCurrentModal(null));
                dispatch(setCheckoutInProgress(true));
                resumeRedcatPaymentStatusPolling();
              },
            },
          ],
        },
      }),
    );
  }, [redcatPayTimedOut]);

  // Resetting showAppleGooglePayBtn to false if purchaser detail changes so that we can regenerate the pay secret for TYRO
  useEffect(() => {
    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && showAppleGooglePayBtn) {
      setShowAppleGooglePayBtn(false);
    }
  }, [purchaserDetails, payingWithGoogleOrApplePay]);

  /* 
    For credit card payment we need to generate the card when we have all the details filled at the start 
    but after that if the details changes we can handle that with new validate sale call
    before making the payment to tyro so this will give BE the new updated details
    */
  useEffect(() => {
    const shouldRegeneratePaySecretForCreditCard =
      selectedPaymentMethods.some(({ method }) => [PAYMENT_METHOD.CREDIT_CARD].includes(method)) &&
      !hasPaySecret &&
      !purchaserInvalidReasons.length;

    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && shouldRegeneratePaySecretForCreditCard) {
      setExternalDependency({
        ...purchaserDetails,
      });
    }
  }, [selectedPaymentMethods, purchaserDetails, purchaserInvalidReasons]);

  return (
    <>
      <div ref={tyroJSRef}>
        <form id="pay-form">
          <div
            style={combineStyles(
              !useMobileScreen && styles.mainContainerDesktop,
              p('screen', ['backgroundColor']),
              p('reviewOrderScreen', ['backgroundColor']),

              !useMobileScreen && p('reviewOrderScreenDesktop', ['backgroundColor', 'boxShadow']),
              useMobileScreen && p('reviewOrderScreenMobile', ['backgroundColor']),
            )}
          >
            <OrderingWrapper
              title={t('title.checkout')}
              bottomContainer={
                member && mustBeVerifiedMember ? (
                  <>
                    <div style={styles.mustSignInSection}>
                      <Text style={p('reviewOrderUnverifiedText', ['color'])}>
                        {t('memberVerificationRequirment')}
                      </Text>
                    </div>
                    <SendVerificationButton />
                  </>
                ) : (
                  <OrderTotals pointsVisible />
                )
              }
            >
              <OrderSummary />
            </OrderingWrapper>
          </div>
          <PlaceOrderButton
            checkoutSuccessful={checkoutSuccessful}
            paymentStatusPolling={
              tyroPaymentOrderId && paymentStatusPollCount.current < tyroPollingRules.maxCount
            }
          />
        </form>
      </div>
    </>
  );
};

const styles: Styles = {
  mainContainerMobile: {},

  desktopParentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  mainContainerDesktop: {
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: 660,
    margin: '50px 30px 50px',
  },

  mobileCheckoutButton: {
    width: 250,
  },

  mustSignInSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
  },
};

export default ReviewOrderScreen;
